
import { Dealer } from "@/types/dealer";
import { Coordinates } from "../types";
import { 
  formatDealersData,
  sortDealersByPriority
} from "../utils";
import { 
  getCachedRecommendedDealers,
  cacheRecommendedDealers,
  invalidateAllDealerCaches
} from "../cache";
import { fetchDataFromSources } from "./dataSources";

/**
 * Core implementation of recommended dealers data fetching with processing
 * CRITICAL: Preserves exact rating and buySpread values
 */
export const fetchDealersCore = async (
  selectedMetal: string,
  coordinates?: Coordinates | null,
  isRefresh: boolean = false,
  signal?: AbortSignal
): Promise<Dealer[]> => {
  try {
    console.log(`Fetching dealers core for metal: ${selectedMetal}, isRefresh: ${isRefresh}`);
    
    // Only invalidate cache if isRefresh is true
    if (isRefresh) {
      console.log("Forcing data refresh, invalidating cache");
      invalidateAllDealerCaches();
    }
    
    // Fetch fresh data from the sources
    const { dealersData } = await fetchDataFromSources(selectedMetal, signal);
    
    if (!dealersData || dealersData.length === 0) {
      console.log("No dealers data returned from sources");
      return [];
    }
    
    console.log(`Received ${dealersData.length} dealers from sources`);
    console.log('RAW DEALERS DATA FOR FORMATTING:', dealersData.slice(0, 5).map(d => ({
      id: d.id,
      name: d.name,
      rating: d.rating,
      rating_type: typeof d.rating,
      buySpread: d.buySpread,
      buySpread_type: typeof d.buySpread
    })));
    
    // Format the dealers data - PRESERVE EXACT VALUES
    let formattedDealers = formatDealersData(dealersData, selectedMetal);
    
    // Log formatted dealers for verification
    console.log("FORMATTED DEALERS AFTER PROCESSING:", formattedDealers.slice(0, 5).map(d => ({
      id: d.id,
      name: d.name,
      rating: d.rating,
      rating_type: typeof d.rating,
      buySpread: d.buySpread,
      buySpread_type: typeof d.buySpread
    })));
    
    // Sort dealers by priority (spread, rating, distance) and limit to max 3
    let sortedDealers = sortDealersByPriority(formattedDealers, coordinates);
    
    // Cache the results if we have data
    if (sortedDealers.length > 0) {
      cacheRecommendedDealers(selectedMetal, sortedDealers);
      console.log(`Cached ${sortedDealers.length} dealers for ${selectedMetal}`);
    }
    
    // Final verification of data before returning
    console.log("FINAL DEALER DATA SAMPLE:", sortedDealers.slice(0, 5).map(d => ({
      id: d.id,
      name: d.name,
      rating: d.rating,
      rating_type: typeof d.rating,
      buySpread: d.buySpread,
      buySpread_type: typeof d.buySpread
    })));
    
    return sortedDealers;
  } catch (error) {
    console.error('Error in fetchDealersCore:', error);
    return [];
  }
};
